import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'
import {
  markSupportBookedEventAsTest,
  markSupportBookedEventCompleted,
  sendSupportBookedEventJoinLink,
  updateSupportBookedEventRtcProvider,
} from 'src/supportPortal/services/supportBookedEventActionsService'

const RTC_PROVIDER_LABELS = {
  agora: 'Agora',
  hms: '100ms',
  daily: 'Daily',
  zoom: 'Zoom',
}

function isBookedEventCompleted(bookedEvent) {
  return Boolean(
    bookedEvent?.markedCompleted ||
      bookedEvent?.sessionStatus === 'marked-completed' ||
      bookedEvent?.sessionStatus === 'finished',
  )
}

/**
 * The per-card "More" menu on the support booked-events page. Owns the lightweight
 * confirm/send dialogs (mark completed, join link, video provider, test flag); heavier flows
 * (reassign drawer, mark-as-paid dialog, detail drawer) are lifted to the page via callbacks.
 */
export default function SupportBookedEventManageMenu({
  bookedEvent,
  BookingActionButton,
  canMarkPaid = false,
  onReassign,
  onMarkPaid,
  onOpenDetails,
  onActionSuccess,
}) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [joinLinkDialogOpen, setJoinLinkDialogOpen] = useState(false)
  const [joinLinkKind, setJoinLinkKind] = useState('secure')
  const [joinLinkCustomMobile, setJoinLinkCustomMobile] = useState('')
  const [joinLinkSending, setJoinLinkSending] = useState(false)
  const [rtcDialogOpen, setRtcDialogOpen] = useState(false)
  const [rtcSelection, setRtcSelection] = useState('')
  const [rtcSaving, setRtcSaving] = useState(false)
  const [testToggling, setTestToggling] = useState(false)

  const isCancelled = bookedEvent?.status === 'cancelled'
  const isCompleted = isBookedEventCompleted(bookedEvent)
  const isPaymentPending =
    bookedEvent?.status === 'booking-initiated' && bookedEvent?.paymentStatus === 'pending'
  const isOnline = bookedEvent?.meetingSrc === 'aumhum'
  const isTestEvent = Boolean(bookedEvent?.testEvent)

  const canMarkCompleted = !isCancelled && !isCompleted && bookedEvent?.status === 'booked'
  const canReassign = !isCancelled && !isCompleted && Boolean(onReassign)
  const showMarkPaid = canMarkPaid && isPaymentPending && !isCancelled && Boolean(onMarkPaid)
  const canSendJoinLink = isOnline && !isCancelled
  const rtcProviderOptions = bookedEvent?.validRtcProviderOptions || []
  const canChangeRtcProvider =
    isOnline && bookedEvent?.rtcProviderOverrideEditable === true && rtcProviderOptions.length > 0

  if (!BookingActionButton) {
    return null
  }

  const closeMenu = () => setMenuAnchorEl(null)

  async function handleMarkCompleted() {
    setCompleting(true)
    const result = await markSupportBookedEventCompleted(bookedEvent?.id)
    setCompleting(false)
    setConfirmCompleteOpen(false)
    if (result.ok) {
      toast.success('Session marked completed')
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  async function handleSendJoinLink(toHumanSupport = false) {
    setJoinLinkSending(true)
    const result = await sendSupportBookedEventJoinLink({
      bookedEventId: bookedEvent?.id,
      kind: joinLinkKind,
      toCustomMobile: joinLinkCustomMobile.trim() || undefined,
      toHumanSupport,
    })
    setJoinLinkSending(false)
    if (result.ok) {
      toast.success(
        toHumanSupport
          ? 'Join link sent to the human-support WhatsApp'
          : joinLinkCustomMobile.trim()
            ? 'Join link sent to the custom number'
            : "Join link sent to the patient's WhatsApp",
      )
      setJoinLinkDialogOpen(false)
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  async function handleRtcProviderSave() {
    setRtcSaving(true)
    const result = await updateSupportBookedEventRtcProvider({
      bookedEventId: bookedEvent?.id,
      rtcProviderOverride: rtcSelection || null,
    })
    setRtcSaving(false)
    if (result.ok) {
      toast.success('Video provider updated')
      setRtcDialogOpen(false)
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  async function handleToggleTestFlag() {
    setTestToggling(true)
    const result = await markSupportBookedEventAsTest(bookedEvent?.id, !isTestEvent)
    setTestToggling(false)
    closeMenu()
    if (result.ok) {
      toast.success(isTestEvent ? 'Test flag removed' : 'Marked as a test booking')
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  return (
    <>
      <BookingActionButton
        label="More"
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
        tone="default"
        startIcon={<MoreHorizRoundedIcon sx={{ fontSize: 17 }} />}
        title="More support actions for this booking"
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        slotProps={{ paper: { sx: { borderRadius: 2.5, minWidth: 250 } } }}
      >
        {onOpenDetails ? (
          <MenuItem
            onClick={() => {
              closeMenu()
              onOpenDetails(bookedEvent)
            }}
          >
            <ListItemIcon>
              <InfoOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="View full details" secondary="Schedule, payment, links, history" />
          </MenuItem>
        ) : null}
        {onOpenDetails ? <Divider /> : null}
        {canMarkCompleted ? (
          <MenuItem
            onClick={() => {
              closeMenu()
              setConfirmCompleteOpen(true)
            }}
          >
            <ListItemIcon>
              <CheckCircleOutlineRoundedIcon fontSize="small" sx={{ color: '#047857' }} />
            </ListItemIcon>
            <ListItemText primary="Mark completed" secondary="On behalf of the practitioner" />
          </MenuItem>
        ) : null}
        {canReassign ? (
          <MenuItem
            onClick={() => {
              closeMenu()
              onReassign(bookedEvent)
            }}
          >
            <ListItemIcon>
              <SwapHorizRoundedIcon fontSize="small" sx={{ color: '#0e7490' }} />
            </ListItemIcon>
            <ListItemText primary="Reassign practitioner" />
          </MenuItem>
        ) : null}
        {showMarkPaid ? (
          <MenuItem
            onClick={() => {
              closeMenu()
              onMarkPaid(bookedEvent)
            }}
          >
            <ListItemIcon>
              <PaidRoundedIcon fontSize="small" sx={{ color: '#047857' }} />
            </ListItemIcon>
            <ListItemText primary="Mark as paid" secondary="Confirm an offline payment" />
          </MenuItem>
        ) : null}
        {canSendJoinLink ? (
          <MenuItem
            onClick={() => {
              closeMenu()
              setJoinLinkKind('secure')
              setJoinLinkCustomMobile('')
              setJoinLinkDialogOpen(true)
            }}
          >
            <ListItemIcon>
              <LinkRoundedIcon fontSize="small" sx={{ color: '#1d4ed8' }} />
            </ListItemIcon>
            <ListItemText primary="Send join link" secondary="WhatsApp, secure or public" />
          </MenuItem>
        ) : null}
        {canChangeRtcProvider ? (
          <MenuItem
            onClick={() => {
              closeMenu()
              setRtcSelection(bookedEvent?.rtcProviderOverride || '')
              setRtcDialogOpen(true)
            }}
          >
            <ListItemIcon>
              <VideocamOutlinedIcon fontSize="small" sx={{ color: '#7c3aed' }} />
            </ListItemIcon>
            <ListItemText
              primary="Change video provider"
              secondary={
                bookedEvent?.rtcProviderResolved
                  ? `Current: ${RTC_PROVIDER_LABELS[bookedEvent.rtcProviderResolved] || bookedEvent.rtcProviderResolved}`
                  : undefined
              }
            />
          </MenuItem>
        ) : null}
        <Divider />
        <MenuItem onClick={handleToggleTestFlag} disabled={testToggling}>
          <ListItemIcon>
            <ScienceOutlinedIcon fontSize="small" sx={{ color: '#b45309' }} />
          </ListItemIcon>
          <ListItemText
            primary={isTestEvent ? 'Remove test flag' : 'Mark as test booking'}
            secondary={isTestEvent ? 'Counted in stats again' : 'Excluded from stats'}
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmCompleteOpen}
        onClose={completing ? undefined : () => setConfirmCompleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Mark this session completed?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            This finishes the session on behalf of the practitioner and triggers the usual
            post-session flows. It cannot be undone from this page.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmCompleteOpen(false)} disabled={completing}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleMarkCompleted} disabled={completing}>
            {completing ? 'Marking...' : 'Mark Completed'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={joinLinkDialogOpen}
        onClose={joinLinkSending ? undefined : () => setJoinLinkDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Send join link over WhatsApp</DialogTitle>
        <DialogContent>
          <Stack spacing={1.75} sx={{ mt: 0.5 }}>
            <ToggleButtonGroup
              value={joinLinkKind}
              exclusive
              size="small"
              fullWidth
              onChange={(event, nextValue) => {
                if (nextValue) {
                  setJoinLinkKind(nextValue)
                }
              }}
            >
              <ToggleButton value="secure">Secure link</ToggleButton>
              <ToggleButton value="public">Public link</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              {joinLinkKind === 'secure'
                ? 'Secure links sign the patient in before joining — preferred.'
                : 'Public links open the session without sign-in. Use when the patient cannot log in.'}
            </Typography>
            <TextField
              label="Custom mobile (optional)"
              size="small"
              fullWidth
              value={joinLinkCustomMobile}
              onChange={(event) => setJoinLinkCustomMobile(event.target.value)}
              placeholder="Leave empty to send to the patient"
              helperText="Include the country code, e.g. +91XXXXXXXXXX"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            color="inherit"
            onClick={() => setJoinLinkDialogOpen(false)}
            disabled={joinLinkSending}
          >
            Cancel
          </Button>
          <Button onClick={() => handleSendJoinLink(true)} disabled={joinLinkSending}>
            To Human Support
          </Button>
          <Button variant="contained" onClick={() => handleSendJoinLink(false)} disabled={joinLinkSending}>
            {joinLinkSending
              ? 'Sending...'
              : joinLinkCustomMobile.trim()
                ? 'Send to Number'
                : 'Send to Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rtcDialogOpen}
        onClose={rtcSaving ? undefined : () => setRtcDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Change video provider</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              Overrides which video-call provider this session uses. Only possible until the
              joining link expires.
            </Typography>
            <TextField
              select
              label="Provider"
              size="small"
              fullWidth
              value={rtcSelection}
              onChange={(event) => setRtcSelection(event.target.value)}
            >
              <MenuItem value="">Default (no override)</MenuItem>
              {rtcProviderOptions.map((provider) => (
                <MenuItem key={provider} value={provider}>
                  {RTC_PROVIDER_LABELS[provider] || provider}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setRtcDialogOpen(false)} disabled={rtcSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRtcProviderSave} disabled={rtcSaving}>
            {rtcSaving ? 'Saving...' : 'Save Provider'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
